import React, { useCallback, useEffect, useState } from 'react';
import { MdSwapHoriz } from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import OdooAutocomplete from './OdooAutocomplete';
import { fetchWithHrToken } from '../../utils/hrApiHelpers';

function todayIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function TransferStudentModal({
  open,
  studentId,
  ensureHrToken,
  onClose,
  onSuccess,
}) {
  const [tab, setTab] = useState('current');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [preview, setPreview] = useState('Please select target faculty and batch');
  const [form, setForm] = useState({
    target_faculty_id: '',
    target_department_id: '',
    target_batch_id: '',
    transfer_date: todayIso(),
    transfer_reason: '',
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const load = useCallback(async () => {
    if (!open || !studentId) return;
    setLoading(true);
    setError(null);
    setConfirmOpen(false);
    setTab('current');
    setForm({
      target_faculty_id: '',
      target_department_id: '',
      target_batch_id: '',
      transfer_date: todayIso(),
      transfer_reason: '',
    });
    setPreview('Please select target faculty and batch');
    setDepartments([]);
    setBatches([]);

    const infoRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getStudentTransfer(Number(studentId), token)
    );
    if (infoRes.error) {
      setError(infoRes.error);
      setLoading(false);
      return;
    }
    const transfer = infoRes.data?.transfer || infoRes.data;
    setInfo(transfer);
    if (transfer?.transfer_date) {
      setForm((prev) => ({ ...prev, transfer_date: String(transfer.transfer_date).slice(0, 10) }));
    }

    const facRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getFaculties(token)
    );
    setFaculties(facRes.data?.faculties || facRes.data?.records || []);
    setLoading(false);
  }, [ensureHrToken, open, studentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open || !form.target_faculty_id) {
      setDepartments([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getDepartments(form.target_faculty_id, token)
      );
      if (cancelled) return;
      setDepartments(data?.departments || data?.records || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, open, form.target_faculty_id]);

  useEffect(() => {
    if (!open || !form.target_department_id) {
      setBatches([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getBatches(form.target_department_id, token, {
          faculty_id: form.target_faculty_id || undefined,
        })
      );
      if (cancelled) return;
      setBatches(data?.batches || data?.records || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, open, form.target_department_id, form.target_faculty_id]);

  useEffect(() => {
    if (!open || !studentId) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.previewStudentTransfer(
          {
            student_id: Number(studentId),
            target_faculty_id: form.target_faculty_id || null,
            target_batch_id: form.target_batch_id || null,
          },
          token
        )
      );
      if (cancelled) return;
      setPreview(data?.new_student_no_preview || 'Please select target faculty and batch');
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ensureHrToken, open, studentId, form.target_faculty_id, form.target_batch_id]);

  if (!open) return null;

  const formReady =
    Boolean(form.target_faculty_id) &&
    Boolean(form.target_department_id) &&
    Boolean(form.target_batch_id) &&
    Boolean(String(form.transfer_reason || '').trim());

  const openConfirm = () => {
    if (busy) return;
    if (!formReady) {
      setError('Please select target faculty, department, batch, and enter a transfer reason.');
      setTab('to');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const runTransfer = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.confirmStudentTransfer(
        {
          student_id: Number(studentId),
          target_faculty_id: Number(form.target_faculty_id),
          target_department_id: Number(form.target_department_id),
          target_batch_id: Number(form.target_batch_id),
          transfer_date: form.transfer_date,
          transfer_reason: form.transfer_reason.trim(),
        },
        token
      )
    );
    setBusy(false);
    if (fetchError) {
      setConfirmOpen(false);
      setError(fetchError);
      return;
    }
    setConfirmOpen(false);
    onSuccess?.(data);
    onClose?.();
  };

  return (
    <div className="ea-odoo-modal-backdrop" role="presentation" onClick={() => !busy && onClose?.()}>
      <div
        className="ea-odoo-modal ea-transfer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ea-transfer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ea-odoo-modal__header">
          <h2 id="ea-transfer-title">Transfer Student</h2>
          <button
            type="button"
            className="ea-odoo-modal__close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="ea-odoo-modal__body ea-transfer-modal__body">
          <div className="ea-transfer-banner">
            <MdSwapHoriz size={22} aria-hidden />
            <div>
              <strong>Internal Student Transfer</strong>
              <p>Transfer a student to a different faculty, department, or batch within the university</p>
            </div>
          </div>

          {error ? (
            <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
              {error}
            </div>
          ) : null}

          {loading ? (
            <p className="ea-odoo-muted">Loading transfer details…</p>
          ) : (
            <>
              <section className="ea-transfer-section">
                <h3>Student Information</h3>
                <div className="ea-transfer-kv">
                  <div>
                    <span>Student Name</span>
                    <strong>{info?.student_name || '—'}</strong>
                  </div>
                  <div>
                    <span>Admission Number</span>
                    <strong>{info?.admission_number || '—'}</strong>
                  </div>
                </div>
              </section>

              {(info?.warnings || []).map((w) => (
                <div key={w} className="ea-odoo-alert ea-odoo-alert--warning" role="status">
                  {w}
                </div>
              ))}

              <div className="ea-transfer-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  className={`ea-transfer-tabs__btn${tab === 'current' ? ' active' : ''}`}
                  aria-selected={tab === 'current'}
                  onClick={() => setTab('current')}
                >
                  Current Details
                </button>
                <button
                  type="button"
                  role="tab"
                  className={`ea-transfer-tabs__btn${tab === 'to' ? ' active' : ''}`}
                  aria-selected={tab === 'to'}
                  onClick={() => setTab('to')}
                >
                  Transfer To
                </button>
              </div>

              {tab === 'current' ? (
                <section className="ea-transfer-section">
                  <h3>Current Assignment</h3>
                  <dl className="ea-transfer-dl">
                    <div>
                      <dt>Current Faculty</dt>
                      <dd>{info?.current_faculty_name || '—'}</dd>
                    </div>
                    <div>
                      <dt>Current Department</dt>
                      <dd>{info?.current_department_name || '—'}</dd>
                    </div>
                    <div>
                      <dt>Current Batch</dt>
                      <dd>{info?.current_batch_name || '—'}</dd>
                    </div>
                    <div>
                      <dt>Current Student ID</dt>
                      <dd>{info?.current_student_no || '—'}</dd>
                    </div>
                  </dl>
                </section>
              ) : (
                <div className="ea-transfer-grid">
                  <section className="ea-transfer-section">
                    <h3>New Assignment</h3>
                    <label className="ea-transfer-field">
                      <span>Transfer to Faculty</span>
                      <OdooAutocomplete
                        value={form.target_faculty_id}
                        options={faculties}
                        placeholder="Select target faculty..."
                        aria-label="Transfer to Faculty"
                        onChange={(id) => {
                          setField('target_faculty_id', id);
                          setField('target_department_id', '');
                          setField('target_batch_id', '');
                        }}
                      />
                    </label>
                    <label className="ea-transfer-field">
                      <span>Transfer to Department</span>
                      <OdooAutocomplete
                        value={form.target_department_id}
                        options={departments}
                        disabled={!form.target_faculty_id}
                        placeholder="Select target department..."
                        aria-label="Transfer to Department"
                        onChange={(id) => {
                          setField('target_department_id', id);
                          setField('target_batch_id', '');
                        }}
                      />
                    </label>
                    <label className="ea-transfer-field">
                      <span>Transfer to Batch</span>
                      <OdooAutocomplete
                        value={form.target_batch_id}
                        options={batches}
                        disabled={!form.target_department_id}
                        placeholder="Select target batch..."
                        aria-label="Transfer to Batch"
                        onChange={(id) => setField('target_batch_id', id)}
                      />
                    </label>
                    <div className="ea-transfer-field">
                      <span>New Student ID (Preview)</span>
                      <p className="ea-transfer-preview">{preview}</p>
                    </div>
                  </section>

                  <section className="ea-transfer-section">
                    <h3>Transfer Information</h3>
                    <label className="ea-transfer-field">
                      <span>Transfer Date</span>
                      <input
                        type="date"
                        value={form.transfer_date}
                        onChange={(e) => setField('transfer_date', e.target.value)}
                      />
                    </label>
                    <label className="ea-transfer-field">
                      <span>Transfer Reason</span>
                      <textarea
                        rows={5}
                        value={form.transfer_reason}
                        onChange={(e) => setField('transfer_reason', e.target.value)}
                        placeholder="Please provide a detailed reason for this transfer..."
                      />
                    </label>
                  </section>
                </div>
              )}

              <div className="ea-odoo-alert ea-odoo-alert--warning ea-transfer-important" role="note">
                <strong>Important:</strong> This action will update the student&apos;s faculty, department,
                batch, and student ID. A complete history will be maintained for auditing purposes.
              </div>
            </>
          )}
        </div>

        <div className="ea-odoo-modal__footer">
          <button
            type="button"
            className="ea-odoo-btn ea-odoo-btn--danger"
            disabled={busy || loading || !formReady}
            title={
              !formReady
                ? 'Select faculty, department, batch and enter a reason on the Transfer To tab'
                : undefined
            }
            onClick={openConfirm}
          >
            {busy ? 'Transferring…' : 'Confirm Transfer'}
          </button>
          <button
            type="button"
            className="ea-odoo-btn ea-odoo-btn--ghost"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          {!loading && !formReady ? (
            <span className="ea-transfer-footer-hint">
              Fill Faculty, Department, Batch and Reason on Transfer To to enable Confirm.
            </span>
          ) : null}
        </div>
      </div>

      {confirmOpen ? (
        <div
          className="ea-odoo-modal-backdrop ea-odoo-modal-backdrop--nested"
          role="presentation"
          onClick={() => !busy && setConfirmOpen(false)}
        >
          <div
            className="ea-odoo-modal ea-odoo-modal--confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-transfer-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-transfer-confirm-title">Confirmation</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>
                Are you sure you want to transfer this student? This action will change their
                faculty, department, batch, and student ID.
              </p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={busy}
                onClick={runTransfer}
              >
                {busy ? 'Transferring…' : 'Ok'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
